import React, { useState, useMemo } from 'react';
import { Table, Icon, Input, Message, Dimmer, Loader, Popup } from 'semantic-ui-react';
import { SpreadingGround, SpreadingGroundBasinTypeId } from "../../../models/SpreadingGround";

interface ISGUITableProps {
    spreadingGroundsList?: SpreadingGround[];
    editSG: (sg: SpreadingGround) => void;
    deleteSG: (sg: SpreadingGround) => void;
    viewCapture: (sg: SpreadingGround) => void;
    isLoading: boolean;
}

const SGUITable = (props: ISGUITableProps) => {
    const [searchTerm, setSearchTerm] = useState('');

    const lastUploadDate = useMemo(() => {
        if (!props.spreadingGroundsList || props.spreadingGroundsList.length === 0) return null;

        const allUpdatedDates = props.spreadingGroundsList.flatMap(sg => sg.spreadingGroundCapture).map(x => new Date(x.updated));

        return allUpdatedDates.reduce((max, x) => x.getTime() > (max?.getTime() ?? 0) ? x : max, null);
    }, [props.spreadingGroundsList]);

    const filteredData = useMemo(() => {
        if (!props.spreadingGroundsList) return [];
        if (!searchTerm) return props.spreadingGroundsList;

        return props.spreadingGroundsList.filter(sg =>
            sg.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            sg.waterSourceDescription?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            sg.firstSeason?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            SpreadingGroundBasinTypeId[sg.basinTypeId]?.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [props.spreadingGroundsList, searchTerm]);

    const getMostRecentEvent = (sg: SpreadingGround) => {
        if (!sg.spreadingGroundCapture || sg.spreadingGroundCapture.length === 0) {
            return <div>No Capture Events</div>;
        }

        const mostRecentUpdate = sg.spreadingGroundCapture.reduce((acuRecord, currentRecord) => {
            return new Date(acuRecord.updated) > new Date(currentRecord.updated) ? acuRecord : currentRecord;
        });

        return (
            <div>
                <p>
                    {new Date(mostRecentUpdate?.spreadingGroundCaptureTimeFrame.startDate).toLocaleString([], { year: 'numeric', month: '2-digit', day: '2-digit' })} - {new Date(mostRecentUpdate?.spreadingGroundCaptureTimeFrame.endDate).toLocaleString([], { year: 'numeric', month: '2-digit', day: '2-digit' })}
                </p>
                <p>Capture: {mostRecentUpdate?.waterConservedAcft} ac-ft</p>
            </div>
        );
    };

    return (
        <div className="sgui-table">
            <div className="sgui-table__header">
                <Input
                    icon='search'
                    placeholder='Search Spreading Grounds...'
                    value={searchTerm}
                    onChange={(e, { value }) => setSearchTerm(value)}
                    style={{ width: '300px', height: '42px' }}

                />
                {lastUploadDate && (
                    <Message style={{ display: "flex", alignItems: "center", width: "fit-content", height: '42px', margin: "0 0 0 1.5rem", padding: "1rem 1rem 0.9rem 1rem" }}>
                        Last Upload: <b> {lastUploadDate.toLocaleString([], { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</b>
                    </Message>
                )}
            </div>
            <Dimmer.Dimmable>
                <Dimmer active={props.isLoading} inverted>
                    <Loader />
                </Dimmer>

                <Table celled striped>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell>Spreading Ground Name</Table.HeaderCell>
                            <Table.HeaderCell>Basin Type</Table.HeaderCell>
                            <Table.HeaderCell>First Season</Table.HeaderCell>
                            <Table.HeaderCell>Intake Capacity (cfs)</Table.HeaderCell>
                            <Table.HeaderCell>Most Recent Event</Table.HeaderCell>
                            <Table.HeaderCell>Water Source</Table.HeaderCell>
                            <Table.HeaderCell>Actions</Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>

                    <Table.Body>
                        {filteredData.map((sg, index) => (
                            <Table.Row key={index}>
                                <Table.Cell>{sg.name}</Table.Cell>
                                <Table.Cell>{SpreadingGroundBasinTypeId[sg.basinTypeId]}</Table.Cell>
                                <Table.Cell>{sg.firstSeason}</Table.Cell>
                                <Table.Cell>{sg.intakeCapacityCfs}</Table.Cell>
                                <Table.Cell width="2">{getMostRecentEvent(sg)}</Table.Cell>
                                <Table.Cell>{sg.waterSourceDescription}</Table.Cell>
                                <Table.Cell className="table-cell--actions">
                                    <div className="icons-wrapper">
                                        <Popup
                                            trigger={
                                                <Icon
                                                    name="pencil"
                                                    onClick={() => props.editSG(sg)}
                                                />
                                            }>
                                            <Popup.Content>
                                                Edit Spreading Ground
                                            </Popup.Content>
                                        </Popup>
                                        <Popup
                                            trigger={
                                                <Icon
                                                    name="eye"
                                                    onClick={() => props.viewCapture(sg)}
                                                />
                                            }>
                                            <Popup.Content>
                                                View Capture
                                            </Popup.Content>
                                        </Popup>
                                        <Popup
                                            trigger={
                                                <Icon
                                                    name="trash"
                                                    onClick={() => props.deleteSG(sg)}
                                                />
                                            }>
                                            <Popup.Content>
                                                Delete Spreading Ground
                                            </Popup.Content>
                                        </Popup>
                                    </div>
                                </Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            </Dimmer.Dimmable>

            {
                !props.isLoading && filteredData.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '2em', color: '#999' }}>
                        {searchTerm ? 'No Spreading Grounds found matching your search.' : 'No Spreading Grounds available.'}
                    </div>
                )
            }
        </div >
    );
};

export default SGUITable;
